// src/pages/dashboard/Settings.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getUserMe,
  logoutUser
} from '../../api/userService';

import Loader from '../../components/Loader';
import Notification from '../../components/misc/Notification';

import ProfileCard from '../../components/Settings/ProfileCard';
import SettingsPanel from '../../components/Settings/SettingsPanel';
import PasswordPanel from '../../components/Settings/PasswordPanel';

interface UserMeResponse {
  email: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  born_date?: string;
  language_code?: string;
}

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Estados
  const [userData, setUserData] = useState<UserMeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Notificación
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Al montar, obtenemos info del usuario
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError('');

        const data = await getUserMe(); // llama a /users/me
        if (!data) {
          setError(t('settings.errors.userFetch'));
        } else {
          setUserData(data);
          // Ajustar idioma en la app si el usuario tiene language_code
          if (data.language_code) {
            i18n.changeLanguage(data.language_code);
          }
        }
      } catch (err: any) {
        setError(t('settings.errors.userFetchGeneric'));
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []); 

  const refreshUserData = async () => {
    try {
      const updatedData = await getUserMe();
      if (updatedData) {
        setUserData(updatedData);
        // Ajustar idioma si la BD lo cambió
        if (updatedData.language_code) {
          i18n.changeLanguage(updatedData.language_code);
        }
      }
    } catch (err) {
      console.error('Error refreshUserData:', err);
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      setError('');
      setNotificationMessage(t('settings.notifications.logoutSuccess'));
      setShowNotification(true);
      setTimeout(async () => {
        await logoutUser();
      }, 2000);
    } catch (err: any) {
      setError(t('settings.errors.logout'));
    }
  };
  

  // Render principal
  if (loading) return <Loader />;

  return (
    <div className="p-6 flex flex-col gap-6">
      {error && (
        <div className="text-red-500 mb-4">{error}</div>
      )}

      {/* TARJETA DE PERFIL (Cambiar nombre / Logout) */}
      <ProfileCard
        userData={userData}
        refreshUserData={refreshUserData}
        t={t}
        setShowNotification={setShowNotification}
        setNotificationMessage={setNotificationMessage}
        handleLogout={handleLogout}
      />

      {/* Paneles en una fila */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Ajustes de teléfono, fecha, idioma */}
        <SettingsPanel
          userData={userData}
          t={t}
          refreshUserData={refreshUserData}
          setShowNotification={setShowNotification}
          setNotificationMessage={setNotificationMessage}
        />

        {/* Panel de cambio de password */}
        <PasswordPanel
          t={t}
          refreshUserData={refreshUserData}
          setShowNotification={setShowNotification}
          setNotificationMessage={setNotificationMessage}
        />
      </div>

      {/* Notificación de cambios */}
      {showNotification && (
        <Notification
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}
    </div>
  );
};

export default Settings;
